import { displayCategoryName, displayName } from "~/common/functions";
import { GDTF, type TimelineEntity } from "~/components/timeline";
import type { GetRelationsForTimelineQuery } from "~/features/relations/gqls";
import type { Locale } from "~/i18n/resolveLocale";

type RecordForTimeline = GetRelationsForTimelineQuery["records"][number];

const safeParseGDTF = (input: string, isStartDate: boolean): GDTF | null => {
  try {
    const gdtf = new GDTF(input, isStartDate);
    return gdtf.isValid() ? gdtf : null;
  } catch {
    return null;
  }
};

/** Map a timeline record to a `TimelineEntity`. Ported from V1 `recordToTimelineEntity` (pure). */
export function toTimelineRelation(
  record: RecordForTimeline,
  rowNumber: number,
  locale: Locale,
): TimelineEntity | null {
  if (!record.date) return null;

  const startGdtf = safeParseGDTF(record.date, true);
  if (!startGdtf) return null;

  let end: { year: number } | undefined;
  if (record.endDate) {
    const endGdtf = safeParseGDTF(record.endDate, false);
    if (endGdtf) end = { year: endGdtf.year };
  }

  return {
    id: record.id,
    title: displayName(record, locale),
    start: { year: startGdtf.year },
    end,
    categories: record.recordMetadata.recordMetadataCategories.map((rmc) =>
      displayCategoryName(
        { name: rmc.category.name, i18nLabel: rmc.category.i18nLabel as Record<string, unknown> | null },
        locale,
      ),
    ),
    categoryNames: record.recordMetadata.recordMetadataCategories.map((rmc) => rmc.category.name),
    rowNumber,
  };
}
